import { useState, useEffect, useCallback, useRef } from "react";
import { getProjetosCompleto } from "../../api/projetos.api";
import { atualizarTarefa } from "../../api/tarefas.api";

/* ─── Helpers ─────────────────────────────────────────────────────── */

const STATUS_COR = {
  "Nao Iniciada": "#64748b",
  "Em Andamento": "#60a5fa",
  "Bloqueada":    "#f87171",
  "Concluida":    "#4ade80",
};

const STATUS_LABEL = {
  "Nao Iniciada": "Não Iniciada",
  "Em Andamento": "Em Andamento",
  "Bloqueada":    "Bloqueada",
  "Concluida":    "Concluída",
};

const PROXIMAS_ACOES = {
  "Nao Iniciada": [{ label: "Iniciar",  key: "Em Andamento", cor: "#60a5fa" }],
  "Em Andamento": [
    { label: "Concluir", key: "Concluida", cor: "#4ade80" },
    { label: "Bloquear", key: "Bloqueada", cor: "#f87171" },
  ],
  "Bloqueada":    [{ label: "Retomar",  key: "Em Andamento", cor: "#60a5fa" }],
  "Concluida":    [],
};

function fmtDate(str) {
  if (!str) return null;
  const [y, m, d] = str.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

function isAtrasada(tarefa) {
  if (!tarefa.data_entrega || tarefa.status === "Concluida") return false;
  return new Date(tarefa.data_entrega) < new Date(new Date().toDateString());
}

function getDiffDias(str) {
  if (!str) return null;
  const hoje  = new Date(new Date().toDateString());
  const prazo = new Date(str);
  return Math.floor((prazo - hoje) / 86400000);
}

/* ─── Página ─────────────────────────────────────────────────────── */

export default function TarefasResponsaveis() {
  const [tarefas,   setTarefas]   = useState([]);
  const [ticketMap, setTicketMap] = useState({});
  const [loading,   setLoading]   = useState(true);
  const [expandido, setExpandido] = useState({});
  const [countdown, setCountdown] = useState(60);
  const cntRef  = useRef(60);
  const isFirst = useRef(true);

  const load = useCallback(async (bust = false) => {
    try {
      const { projetos: rows, tarefas: allTarefas } = await getProjetosCompleto(bust, 2);
      const tMap = {};
      (rows || []).forEach(r => {
        if (r.ticketid && !tMap[r.ticketid]) {
          tMap[r.ticketid] = {
            id:                    r.ticketid,
            subject:               r.subject || `#${r.ticketid}`,
            descricao_customizada: r.descricao_customizada || null,
            projeto:               r.descricao,
          };
        }
      });
      setTicketMap(tMap);
      const all = allTarefas || [];
      setTarefas(all);

      // Expandir automaticamente quem tem tarefas urgentes
      const urgentes = {};
      all.forEach(t => {
        if (t.responsavel && isAtrasada(t)) urgentes[t.responsavel] = true;
      });
      setExpandido(prev => ({ ...prev, ...urgentes }));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      isFirst.current = false;
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setInterval(() => {
      cntRef.current -= 1;
      setCountdown(cntRef.current);
      if (cntRef.current <= 0) { cntRef.current = 60; setCountdown(60); load(true); }
    }, 1000);
    return () => clearInterval(t);
  }, [load]);

  async function handleStatusChange(tarefa, novoStatus) {
    try {
      await atualizarTarefa(tarefa.id_tarefa, { ...tarefa, status: novoStatus });
      setTarefas(prev => prev.map(t =>
        t.id_tarefa === tarefa.id_tarefa ? { ...t, status: novoStatus } : t
      ));
    } catch { alert("Erro ao atualizar status."); }
  }

  /* ── Agrupamento por responsável ── */
  const semResponsavel = tarefas.filter(t => !t.responsavel && t.status !== "Concluida");
  const porResp = {};
  tarefas.forEach(t => {
    if (!t.responsavel) return;
    if (!porResp[t.responsavel]) porResp[t.responsavel] = [];
    porResp[t.responsavel].push(t);
  });

  // Ordena por: quem tem mais atrasadas primeiro
  const respOrdenados = Object.entries(porResp).sort(([, a], [, b]) => {
    const atA = a.filter(isAtrasada).length;
    const atB = b.filter(isAtrasada).length;
    if (atB !== atA) return atB - atA;
    return b.filter(t => t.status !== "Concluida").length - a.filter(t => t.status !== "Concluida").length;
  });

  if (loading && isFirst.current) {
    return <div style={skeletonPage}><span style={{ color: "#475569" }}>Carregando...</span></div>;
  }

  return (
    <div style={page}>

      {/* ── Header ── */}
      <div style={headerRow}>
        <div>
          <h1 style={pageTitle}>Tarefas por Responsável</h1>
          <div style={pageSub}>{respOrdenados.length} responsáveis · {tarefas.length} tarefas</div>
        </div>
        <button style={refreshBtn} onClick={() => { cntRef.current = 60; setCountdown(60); load(true); }}>
          ↻ {countdown}s
        </button>
      </div>

      {/* ── Cards por responsável ── */}
      <div style={grid}>
        {respOrdenados.map(([nome, tarefasResp]) => {
          const abertas    = tarefasResp.filter(t => t.status !== "Concluida");
          const atrasadas  = abertas.filter(isAtrasada);
          const bloqueadas = abertas.filter(t => t.status === "Bloqueada");
          const concluidas = tarefasResp.filter(t => t.status === "Concluida");
          const pct        = tarefasResp.length > 0
            ? Math.round(concluidas.length / tarefasResp.length * 100) : 0;
          const aberto = expandido[nome];
          const corSaude = atrasadas.length > 0 ? "#f87171"
            : bloqueadas.length > 0 ? "#fb923c"
            : abertas.length > 0 ? "#60a5fa"
            : "#4ade80";

          return (
            <div key={nome} style={respCard(aberto)}>

              {/* Cabeçalho do card */}
              <div
                style={respHeader}
                onClick={() => setExpandido(prev => ({ ...prev, [nome]: !prev[nome] }))}
              >
                <div style={avatar(corSaude)}>{nome.charAt(0).toUpperCase()}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={respNome}>{nome}</div>
                  <div style={respMeta}>
                    {abertas.length} aberta{abertas.length !== 1 ? "s" : ""}
                    {atrasadas.length > 0 && (
                      <span style={{ color: "#f87171", fontWeight: 700 }}> · ⚠ {atrasadas.length} atrasada{atrasadas.length > 1 ? "s" : ""}</span>
                    )}
                    {bloqueadas.length > 0 && (
                      <span style={{ color: "#fb923c" }}> · ⛔ {bloqueadas.length} bloqueada{bloqueadas.length > 1 ? "s" : ""}</span>
                    )}
                  </div>
                </div>

                {/* KPIs compactos */}
                <div style={kpiRow}>
                  {[
                    { label: "Abertas",   val: abertas.length,    cor: "#60a5fa" },
                    { label: "Atrasadas", val: atrasadas.length,  cor: "#f87171" },
                    { label: "Concluídas",val: concluidas.length, cor: "#4ade80" },
                  ].map(k => (
                    <div key={k.label} style={kpiMini}>
                      <span style={{ fontSize: 16, fontWeight: 800, color: k.cor }}>{k.val}</span>
                      <span style={{ fontSize: 9, color: "#475569" }}>{k.label}</span>
                    </div>
                  ))}
                </div>

                <span style={{ color: "#334155", fontSize: 12 }}>{aberto ? "▲" : "▼"}</span>
              </div>

              {/* Barra de progresso */}
              <div style={progressBar}>
                <div style={{ ...progressFill, width: `${pct}%`, background: corSaude }} />
              </div>

              {/* Lista de tarefas (expandida) */}
              {aberto && (
                <div style={tarefaLista}>
                  {tarefasResp
                    .filter(t => t.status !== "Concluida")
                    .sort((a, b) => {
                      if (isAtrasada(a) && !isAtrasada(b)) return -1;
                      if (!isAtrasada(a) && isAtrasada(b)) return 1;
                      if (!a.data_entrega) return 1;
                      if (!b.data_entrega) return -1;
                      return new Date(a.data_entrega) - new Date(b.data_entrega);
                    })
                    .map(t => {
                      const ticket = ticketMap[t.id_ticket] || {};
                      const desc   = ticket.descricao_customizada || ticket.subject || "";
                      const diff   = getDiffDias(t.data_entrega);
                      const atras  = isAtrasada(t);
                      const acoes  = PROXIMAS_ACOES[t.status] || [];
                      return (
                        <div key={t.id_tarefa} style={tarefaItem(atras)}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {ticket.projeto && (
                              <span style={projetoTag}>{ticket.projeto}</span>
                            )}
                            {desc && (
                              <span style={ticketTag}>#{t.id_ticket} · {desc}</span>
                            )}
                            <div style={tarefaDescText}>{t.descritivo}</div>
                            <div style={{ display: "flex", gap: 10, marginTop: 3 }}>
                              <span style={{ fontSize: 10, color: STATUS_COR[t.status] }}>
                                ● {STATUS_LABEL[t.status] || t.status}
                              </span>
                              {t.data_entrega && (
                                <span style={{ fontSize: 10, color: atras ? "#f87171" : diff <= 2 ? "#fbbf24" : "#94a3b8" }}>
                                  📅 {fmtDate(t.data_entrega)}
                                  {atras ? ` (${Math.abs(diff)}d atraso)` : diff === 0 ? " (hoje)" : diff === 1 ? " (amanhã)" : ""}
                                </span>
                              )}
                            </div>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            {acoes.map(a => (
                              <button key={a.key} style={acaoBtn(a.cor)} onClick={() => handleStatusChange(t, a.key)}>
                                → {a.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  {tarefasResp.filter(t => t.status !== "Concluida").length === 0 && (
                    <div style={{ color: "#4ade80", fontSize: 12, padding: "8px 0" }}>
                      ✅ Todas as tarefas concluídas
                    </div>
                  )}
                </div>
              )}

            </div>
          );
        })}
      </div>

      {/* Sem responsável */}
      {semResponsavel.length > 0 && (
        <div style={semRespBox}>
          <div style={{ color: "#fbbf24", fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
            ⚠ {semResponsavel.length} tarefa{semResponsavel.length > 1 ? "s" : ""} sem responsável
          </div>
          {semResponsavel.map(t => {
            const ticket = ticketMap[t.id_ticket] || {};
            return (
              <div key={t.id_tarefa} style={{ fontSize: 12, color: "#94a3b8", padding: "3px 0" }}>
                #{t.id_ticket} · {t.descritivo}
                {ticket.projeto && <span style={{ color: "#475569" }}> ({ticket.projeto})</span>}
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}

/* ─── Estilos ─────────────────────────────────────────────────────── */

const page = { padding: "28px 32px", display: "flex", flexDirection: "column", gap: 20 };

const headerRow = { display: "flex", justifyContent: "space-between", alignItems: "flex-start" };

const pageTitle = { color: "#f1f5f9", fontSize: 22, fontWeight: 700, margin: 0 };

const pageSub = { color: "#475569", fontSize: 13, marginTop: 4 };

const refreshBtn = {
  padding: "8px 14px", borderRadius: 9,
  border: "1px solid rgba(96,165,250,0.2)", background: "rgba(96,165,250,0.05)",
  color: "#60a5fa", fontSize: 12, fontWeight: 600, cursor: "pointer",
};

const grid = { display: "flex", flexDirection: "column", gap: 12 };

const respCard = aberto => ({
  background: "rgba(11,22,42,0.6)",
  border: `1px solid ${aberto ? "rgba(96,165,250,0.2)" : "rgba(96,165,250,0.08)"}`,
  borderRadius: 14,
  overflow: "hidden",
  transition: "border-color 0.15s",
});

const respHeader = {
  display: "flex", alignItems: "center", gap: 12,
  padding: "14px 18px", cursor: "pointer",
};

const avatar = cor => ({
  width: 38, height: 38, borderRadius: "50%",
  background: `${cor}22`, border: `2px solid ${cor}55`,
  display: "flex", alignItems: "center", justifyContent: "center",
  fontSize: 16, fontWeight: 800, color: cor, flexShrink: 0,
});

const respNome = { fontSize: 14, fontWeight: 700, color: "#e2e8f0" };

const respMeta = { fontSize: 11, color: "#475569", marginTop: 2 };

const kpiRow = { display: "flex", gap: 16 };

const kpiMini = { display: "flex", flexDirection: "column", alignItems: "center", gap: 1 };

const progressBar = {
  height: 3, background: "rgba(255,255,255,0.05)",
  margin: "0 18px 0",
};

const progressFill = { height: "100%", borderRadius: 2, transition: "width 0.5s" };

const tarefaLista = {
  display: "flex", flexDirection: "column", gap: 6,
  padding: "8px 18px 14px",
  borderTop: "1px solid rgba(96,165,250,0.08)",
};

const tarefaItem = atras => ({
  display: "flex", alignItems: "flex-start", gap: 10,
  padding: "10px 12px",
  borderRadius: 9,
  background: atras ? "rgba(248,113,113,0.06)" : "rgba(255,255,255,0.02)",
  border: `1px solid ${atras ? "rgba(248,113,113,0.15)" : "rgba(96,165,250,0.06)"}`,
});

const projetoTag = {
  display: "inline-block", fontSize: 9, fontWeight: 600, color: "#22d3ee",
  background: "rgba(34,211,238,0.08)", border: "1px solid rgba(34,211,238,0.18)",
  borderRadius: 4, padding: "1px 5px", marginRight: 5, marginBottom: 3,
};

const ticketTag = {
  display: "inline-block", fontSize: 9, fontWeight: 600, color: "#60a5fa",
  background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.18)",
  borderRadius: 4, padding: "1px 5px", marginBottom: 3,
};

const tarefaDescText = { fontSize: 13, color: "#e2e8f0", fontWeight: 500, lineHeight: 1.4 };

const acaoBtn = cor => ({
  padding: "3px 9px", borderRadius: 6,
  border: `1px solid ${cor}44`, background: `${cor}11`,
  color: cor, fontSize: 10, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
});

const semRespBox = {
  background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.2)",
  borderRadius: 12, padding: "14px 18px",
};

const skeletonPage = { display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" };
